/**
 * Client Info Command
 * Generate sanitized client info for debugging
 */
import { Command } from 'commander';
import { ConfigService } from '../services/config.service.js';
import { ComponentsService } from '../services/components.service.js';
import { IdeDetectionService } from '../services/ide-detection.service.js';
import { ValidationError } from '../errors/index.js';
import { ErrorLogService, type ErrorLogEntry } from '../services/error-log.service.js';
import { LlmProxyService } from '../services/llm-proxy.service.js';
import { confirm } from '@inquirer/prompts';
import os from 'os';
import { promises as fs } from 'fs';
import { basename, join } from 'path';
import { version as nodeVersion } from 'process';
import packageJson from '../../package.json' with { type: 'json' };

interface ClientInfoOptions {
  json?: boolean;
  full?: boolean;
  submit?: boolean;
  message?: string;
  showPaths?: boolean;
}

interface ClientInfoPayload {
  timestamp: string;
  system: {
    os: string;
    arch: string;
    node: string;
    cliVersion: string | null;
  };
  config: {
    apiUrl: string;
    accessKeyMasked: string;
    frameworkVersion: string | null;
    lastUpdated: string | null;
  };
  project: {
    cwdBase: string;
    cwdFull?: string;
    hasJai1: boolean;
    componentCount: number;
  };
  ide: {
    primary: { id: string; name: string } | null;
    active: Array<{
      id: string;
      name: string;
      ruleCount: number;
      workflowCount: number;
      confidence: 'high' | 'medium' | 'low';
    }>;
  };
  components?: Array<{ filepath: string; version: string; modified: boolean }>;
}

/**
 * Create client-info command
 */
export function createClientInfoCommand(): Command {
  const cmd = new Command('client-info')
    .description('Tạo thông tin client để gửi đội phát triển jai1')
    .option('--json', 'Output as JSON')
    .option('--full', 'Include component list (top 20)')
    .option('--submit', 'Submit client info to Jai1 feedback endpoint')
    .option('--message <text>', 'Additional message when submitting')
    .option('--show-paths', 'Include full project path')
    .action(async (options: ClientInfoOptions) => {
      await handleClientInfo(options);
    });

  return cmd;
}

async function handleClientInfo(options: ClientInfoOptions): Promise<void> {
  // Support global --json from root command as well
  const globalJson = process.argv.includes('--json');
  const useJsonOutput = options.json || globalJson;
  const configService = new ConfigService();
  const config = await configService.load();

  if (!config) {
    throw new ValidationError('Not initialized. Run "jai1 auth" first.');
  }

  const payload = await collectClientInfo(config, options);

  if (useJsonOutput) {
    if (!options.submit) {
      console.log(JSON.stringify(payload, null, 2));
      return;
    }
  } else {
    printTextPayload(payload, options.full === true);
  }

  if (options.submit) {
    const errorLogService = new ErrorLogService();
    const pendingLogs = await errorLogService.readForSubmit({ limitCount: 5, limitBytes: 50_000 });
    const errorLogs: ErrorLogEntry[] = pendingLogs.files.map((f) => f.entry);

    const verbose = process.argv.includes('--verbose');
    const summary = await generateAiSummary(config, payload, errorLogs, verbose);
    const approved = await confirmSubmit(summary, useJsonOutput);
    if (!approved) {
      if (!useJsonOutput) {
        console.log('\n❌ Đã huỷ gửi client info.');
      } else {
        console.log(JSON.stringify({ success: false, cancelled: true }, null, 2));
      }
      return;
    }

    const result = await submitClientInfo(config.apiUrl, config.accessKey, payload, errorLogs, summary);

    if (result.success) {
      await errorLogService.clear(pendingLogs.files.map((f) => f.path));
    }

    if (useJsonOutput) {
      console.log(JSON.stringify({ ...result, error_log_count: errorLogs.length }, null, 2));
      return;
    }

    if (result.success) {
      console.log(`\n✅ Đã gửi client info thành công. ID: ${result.feedback_id}`);
      if (errorLogs.length > 0) {
        console.log(`🧹 Đã xoá ${errorLogs.length} error log(s) sau khi gửi.`);
      }
    } else {
      console.error(`\n❌ Gửi client info thất bại: ${result.error}`);
      process.exit(1);
    }
  }
}

async function collectClientInfo(
  config: { apiUrl: string; accessKey: string; version: string | null; lastUpdated: string | null },
  options: ClientInfoOptions
): Promise<ClientInfoPayload> {
  const cliVersion = packageJson.version ?? null;
  const timestamp = new Date().toISOString();

  const componentsService = new ComponentsService();
  const installedComponents = await componentsService.getInstalled();
  const componentEntries = Object.values(installedComponents);

  const hasJai1 = await hasProjectJai1();
  const projectCwd = process.cwd();

  const ideDetection = new IdeDetectionService(process.cwd());
  const ideSummary = await ideDetection.getSummary();

  const payload: ClientInfoPayload = {
    timestamp,
    system: {
      os: `${os.platform()} ${os.release()}`,
      arch: os.arch(),
      node: nodeVersion,
      cliVersion,
    },
    config: {
      apiUrl: config.apiUrl,
      accessKeyMasked: maskKey(config.accessKey),
      frameworkVersion: config.version,
      lastUpdated: config.lastUpdated,
    },
    project: {
      cwdBase: basename(projectCwd),
      hasJai1,
      componentCount: componentEntries.length,
    },
    ide: {
      primary: ideSummary.primary
        ? { id: ideSummary.primary.id, name: ideSummary.primary.name }
        : null,
      active: ideSummary.active.map((ide) => ({
        id: ide.id,
        name: ide.name,
        ruleCount: ide.ruleCount,
        workflowCount: ide.workflowCount,
        confidence: ide.confidence,
      })),
    },
  };

  if (options.showPaths) {
    payload.project.cwdFull = projectCwd;
  }

  if (options.full) {
    const topComponents = componentEntries
      .sort((a, b) => a.filepath.localeCompare(b.filepath))
      .slice(0, 20)
      .map((item) => ({
        filepath: item.filepath,
        version: item.version,
        modified: item.modified,
      }));

    payload.components = topComponents;
  }

  return payload;
}

async function hasProjectJai1(): Promise<boolean> {
  const projectJai1 = join(process.cwd(), '.jai1');
  try {
    await fs.access(projectJai1);
    return true;
  } catch {
    return false;
  }
}

function maskKey(key: string): string {
  if (key.length <= 4) return '****';
  return '****' + key.slice(-4);
}

function printTextPayload(payload: ClientInfoPayload, includeComponents: boolean): void {
  console.log('JAI1 CLIENT INFO');
  console.log('----------------');
  console.log(`timestamp: ${payload.timestamp}`);

  console.log('\nsystem:');
  console.log(`  os: ${payload.system.os}`);
  console.log(`  arch: ${payload.system.arch}`);
  console.log(`  node: ${payload.system.node}`);
  console.log(`  cli_version: ${payload.system.cliVersion ?? 'unknown'}`);

  console.log('\nconfig:');
  console.log(`  api_url: ${payload.config.apiUrl}`);
  console.log(`  access_key: ${payload.config.accessKeyMasked}`);
  console.log(`  framework_version: ${payload.config.frameworkVersion ?? 'unknown'}`);
  console.log(`  last_updated: ${payload.config.lastUpdated ?? 'unknown'}`);

  console.log('\nproject:');
  console.log(`  cwd: ${payload.project.cwdBase}`);
  if (payload.project.cwdFull) {
    console.log(`  cwd_full: ${payload.project.cwdFull}`);
  }
  console.log(`  jai1_present: ${payload.project.hasJai1}`);
  console.log(`  component_count: ${payload.project.componentCount}`);

  console.log('\nide:');
  if (payload.ide.primary) {
    console.log(`  primary: ${payload.ide.primary.id} (${payload.ide.primary.name})`);
  } else {
    console.log('  primary: none');
  }
  if (payload.ide.active.length === 0) {
    console.log('  active: none');
  } else {
    console.log('  active:');
    for (const ide of payload.ide.active) {
      console.log(
        `    - ${ide.id} (${ide.name}) rules=${ide.ruleCount} workflows=${ide.workflowCount} confidence=${ide.confidence}`
      );
    }
  }

  if (includeComponents && payload.components) {
    console.log('\ncomponents:');
    if (payload.components.length === 0) {
      console.log('  none');
    } else {
      for (const component of payload.components) {
        const modifiedLabel = component.modified ? ' modified' : '';
        console.log(`  - ${component.filepath}@${component.version}${modifiedLabel}`);
      }
    }
  }
}

type FeedbackDraft = {
  type: 'bug' | 'feature' | 'suggestion';
  title: string;
  message: string;
};

const DEFAULT_FEEDBACK: FeedbackDraft = {
  type: 'bug',
  title: 'Client Info Report',
  message: 'Client info auto-generated by jai1 CLI.',
};

async function submitClientInfo(
  apiUrl: string,
  accessKey: string,
  payload: ClientInfoPayload,
  errorLogs: ErrorLogEntry[],
  summary: FeedbackDraft
): Promise<{ success: boolean; feedback_id?: string; error?: string }> {
  const response = await fetch(`${apiUrl}/api/feedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'JAI1-Access-Key': accessKey,
    },
    body: JSON.stringify({
      type: summary.type,
      title: summary.title,
      message: summary.message,
      context: {
        client_info: payload,
        error_logs: errorLogs,
      },
      source: 'cli',
    }),
  });

  const result = await response.json();

  if (!response.ok) {
    return { success: false, error: result.error || 'Failed to submit client info' };
  }

  return { success: true, feedback_id: result.data?.feedback_id };
}

function buildSummaryPrompt(payload: ClientInfoPayload, errorLogs: ErrorLogEntry[]): string {
  return [
    'Bạn là trợ lý kỹ thuật. Hãy tạo JSON hợp lệ với 3 trường: type, title, message.',
    '- type chỉ được là: bug | feature | suggestion.',
    '- title <= 200 ký tự.',
    '- message <= 4000 ký tự, và là tóm tắt lỗi + thông tin client quan trọng.',
    '- message phải là tiếng Việt, ngắn gọn, rõ ràng.',
    '- Không thêm giải thích ngoài JSON.',
    '',
    'Dữ liệu đầu vào (JSON):',
    JSON.stringify({ client_info: payload, error_logs: errorLogs }, null, 2),
  ].join('\n');
}

function parseSummaryDraft(raw: string): FeedbackDraft | null {
  try {
    const cleaned = raw.trim().replace(/^```json\s*/i, '').replace(/```$/i, '');
    const parsed = JSON.parse(cleaned) as FeedbackDraft;
    if (!parsed || typeof parsed !== 'object') return null;
    if (!['bug', 'feature', 'suggestion'].includes(parsed.type)) return null;
    if (!parsed.title || parsed.title.length > 200) return null;
    if (!parsed.message || parsed.message.length > 4000) return null;
    return parsed;
  } catch {
    return null;
  }
}

async function generateAiSummary(
  config: { apiUrl: string; accessKey: string },
  payload: ClientInfoPayload,
  errorLogs: ErrorLogEntry[],
  verbose: boolean
): Promise<FeedbackDraft> {
  const llm = new LlmProxyService({ apiUrl: config.apiUrl, accessKey: config.accessKey } as any);
  const prompt = buildSummaryPrompt(payload, errorLogs);
  try {
    const content = await llm.chat([
      { role: 'system', content: 'Return ONLY valid JSON. Do not add any extra text.' },
      { role: 'user', content: prompt },
    ], 'gemini-3-flash');

    const parsed = parseSummaryDraft(content);
    if (parsed) return parsed;

    if (verbose) {
      console.error('Không parse được JSON từ AI. Raw output:');
      console.error(content.slice(0, 2000));
    }
  } catch (error) {
    if (verbose) {
      console.error('AI summary failed:', error instanceof Error ? error.message : String(error));
    }
  }

  return DEFAULT_FEEDBACK;
}

async function confirmSubmit(summary: FeedbackDraft, jsonMode: boolean): Promise<boolean> {
  if (jsonMode) {
    return true;
  }

  console.log('\nAI SUMMARY (PREVIEW)');
  console.log('---------------------');
  console.log(`type: ${summary.type}`);
  console.log(`title: ${summary.title}`);
  console.log('message:');
  console.log(summary.message);

  return confirm({ message: 'Gửi client info với nội dung trên?', default: true });
}
